import { Avatar, FileButton, Loader, Overlay, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconCamera } from "@tabler/icons-react";
import { profileApi } from "apis/profileApi";
import { storageApi } from "apis/storageApi";
import { ErrorStatusType } from "constants/types/common";
import { FC, useState } from "react";
import { useNotification } from "utils/hooks";
import { useStorageFileUrl } from "utils/hooks/request/useStorageFileUrl";
import classes from "./ProfileAvatarInput.module.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];

interface ProfileAvatarInputProps {
  avatarKey: string | null | undefined;
  avatarLabel: string;
  onUploaded: () => void;
}

const ProfileAvatarInput: FC<ProfileAvatarInputProps> = ({
  avatarKey,
  avatarLabel,
  onUploaded,
}) => {
  const { notification } = useNotification();
  const { url: serverUrl } = useStorageFileUrl(avatarKey);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const displayUrl = previewUrl ?? serverUrl;

  const handleFileChange = async (file: File | null) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      notification.error({ title: "Chỉ chấp nhận ảnh PNG, JPEG hoặc WebP" });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      notification.error({ title: "Ảnh không được vượt quá 5MB" });
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setIsUploading(true);

    try {
      const uploadRes = await storageApi.uploadFile(file, "avatars");
      if (!uploadRes.data?.success) throw new Error();
      const key: string = uploadRes.data.data.key;

      const updateRes = await profileApi.updateAvatar({ avatarKey: key });
      if (!updateRes.data?.success) throw new Error();

      notification.success({ title: "Cập nhật ảnh đại diện thành công" });
      onUploaded();
    } catch (error: ErrorStatusType | any) {
      notification.error({
        title: "Cập nhật ảnh đại diện thất bại",
        message: error?.error?.message,
      });
      URL.revokeObjectURL(localPreview);
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Stack align="center" gap={4}>
      <FileButton onChange={handleFileChange} accept={ALLOWED_TYPES.join(",")}>
        {(props) => (
          <UnstyledButton
            {...props}
            className={classes.avatarButton}
            disabled={isUploading}
            aria-label="Chọn ảnh đại diện"
          >
            <Avatar src={displayUrl} size={80} radius="xl">
              {avatarLabel}
            </Avatar>
            <Overlay className={classes.cameraOverlay} radius="xl">
              {isUploading ? (
                <Loader size="sm" color="white" />
              ) : (
                <IconCamera size={20} color="white" />
              )}
            </Overlay>
          </UnstyledButton>
        )}
      </FileButton>
      <Text size="xs" c="dimmed">
        PNG, JPEG, WebP – tối đa 5MB
      </Text>
    </Stack>
  );
};

export default ProfileAvatarInput;
